import converter from '../../utils/converter';
import CommunicatorClientBase, {IStateBase} from '../device/communicatorClientBase';
import deviceIdlePingScheduler from '../device/deviceIdlePingScheduler';
import {log} from '../log';
import {extendedScpService} from '../scp/extendedScp';
import ConnectedExponentialBackOff from './connectedExponentialBackOff';
import IqosBleClient from './iqosBleClient';

let instance: ScpCharacteristicClient | null = null;

const RESPONSE_TIMEOUT_MS = 5 * 1000;
const WRITE_FRAME_RETRY_COUNT = 1;

interface IState extends IStateBase {
    isFullResponse: boolean;
    isWriteSuccess: boolean;
    isCurrentFrameExtended?: boolean;
    responseFrameHex: string;
    doNotSplit?: boolean;
}

export default class ScpCharacteristicClient extends CommunicatorClientBase<IqosBleClient, IState> {
    constructor(createNew = false) {
        super();

        if (createNew && instance) {
            instance = null;
        }

        if (instance) {
            return instance;
        }

        instance = this;
        this.isActive = true;

        this.iqosClient = new IqosBleClient();
        this.resetState();
        this.addCharacteristicListener();
    }

    disable(): void {
        this.isActive = false;
        this.clearResponseTimeout();
        this.clearQueue();
    }

    getInitialState(): IState {
        return {
            currentFrame: undefined,
            frames: [],
            isFullResponse: true,
            isWriteSuccess: true,
            isCurrentFrameExtended: undefined,
            onError: undefined,
            onSuccess: undefined,
            responseFrameHex: '',
            responses: [],
            timeout: 0,
            wrongHeaderRetryCount: 0,
            skipErrorOnTimeout: undefined,
            isDebug: true,
            retryNumber: 0,
            doNotSplit: undefined,
        };
    }

    resetResponseState(): void {
        this.state.isFullResponse = true;
        this.state.isWriteSuccess = true;
        this.state.responseFrameHex = '';
    }

    private addCharacteristicListener(): void {
        this.iqosClient.addScpCharacteristicListener(this.onScpCharacteristicResponse);
    }

    private onScpCharacteristicResponse = (value: DataView): void => {
        this.clearResponseTimeout();
        const rawResponse = converter.buffer2hex(value);

        this.state.isFullResponse = false;

        const restResponsesCount = value.getUint8(0);
        const isFrameSplitted = restResponsesCount || this.state.responseFrameHex;
        const frameWithoutHeader = isFrameSplitted ? rawResponse.substring(2) : rawResponse;

        this.state.responseFrameHex += frameWithoutHeader;

        this.resetRetries();
        const response = this.getResponseWithAdjustedHeaders();

        log.debug(
            `ScpCharacteristicClient: 
                frame:        ${this.state.currentFrame} 
                raw response: ${rawResponse} 
                rest count:   ${restResponsesCount}` +
                (restResponsesCount
                    ? ''
                    : `
                response:     ${response}`)
        );

        if (restResponsesCount) return;

        this.state.isFullResponse = true;
        this.state.responses.push(response);
        this.writeFrames();
    };

    private getResponseWithAdjustedHeaders = (): string => {
        const {responseFrameHex, isCurrentFrameExtended} = this.state;
        const result = isCurrentFrameExtended
            ? extendedScpService.removeHeaderFromBleExtendedFrame(responseFrameHex)
            : extendedScpService.addHeaderToBleAtomicFrame(responseFrameHex);

        return result;
    };

    writeFrames(forceWriting?: boolean): void {
        const requestIsCancelled = this.stopRequestIfItIsCancelled();

        if (requestIsCancelled) return;

        const {frames, responses, isWriteSuccess, isFullResponse, timeout, doNotSplit} = this.state;

        if ((isWriteSuccess && isFullResponse) || forceWriting) {
            const isPaused = this.pauseRequestIfThereIsAnotherWithHigherPriority();

            if (isPaused) return;

            const responseNumber = responses.length;
            const itWasLastFrame = !frames || responseNumber >= frames.length;

            if (itWasLastFrame) {
                this.onFramesResponse();
            } else {
                const frame = frames[responseNumber];
                const isExtendedFrame = extendedScpService.isBleExtendedFrame(frame);
                const frameToWrite = isExtendedFrame ? extendedScpService.addHeaderToBleAtomicFrame(frame) : frame;

                this.resetResponseState();
                setTimeout(() => this.writeFrame(frameToWrite, isExtendedFrame, doNotSplit), timeout);
            }
        }
    }

    writeFrame = (frame: string, isExtendedFrame: boolean, doNotSplit?: boolean): void => {
        const exponentialBackOff = new ConnectedExponentialBackOff();

        this.state.isWriteSuccess = false;
        this.state.isFullResponse = false;

        this.state.currentFrame = frame;
        this.state.isCurrentFrameExtended = isExtendedFrame;

        const success = () => {
            if (this.isActive && this.iqosClient.isDeviceConnected()) {
                log.debug(`ScpCharacteristicClient: write frame - ${frame}, success`);
                this.state.isWriteSuccess = true;
                this.writeFrames();
            }
        };
        const fail = (e: Error) => {
            this.clearResponseTimeout();
            if (this.isActive) {
                log.debug(`ScpCharacteristicClient: write frame - ${frame}: error: ${e}`);

                this.onFrameResponseError();
            } else {
                this.clearQueue();
            }
        };
        const toTry = async () => {
            if (this.isActive) {
                if (this.iqosClient.isDeviceConnected()) {
                    log.debug(`ScpCharacteristicClient: write frame - ${frame}`);
                    this.initResponseTimeout(frame, exponentialBackOff);
                    deviceIdlePingScheduler.resetScheduler();

                    await this.iqosClient.writeValueToScpCharacteristic(frame, true, doNotSplit);
                } else {
                    log.debug(
                        `ScpCharacteristicClient: writeFrame - writing frame: ${frame} is stopped, device is disconnected`
                    );
                }
            } else {
                this.clearQueue();
            }
        };

        exponentialBackOff.run(3, 200, toTry, success, fail);
    };

    initResponseTimeout = (frame: string, exponentialBackOff: ConnectedExponentialBackOff): void => {
        const {responseTimeoutMs = RESPONSE_TIMEOUT_MS} = this.state;

        this.clearResponseTimeout();
        this.responseTimeout = setTimeout(() => {
            this.onResponseTimeout(frame, exponentialBackOff);
        }, responseTimeoutMs);
    };

    onResponseTimeout = (frame: string, exponentialBackOff: ConnectedExponentialBackOff): void => {
        exponentialBackOff.unsubscribe();

        if (this.isActive) {
            const {retryCount = WRITE_FRAME_RETRY_COUNT, skipErrorOnTimeout, retryNumber} = this.state;

            if (retryCount > retryNumber) {
                this.state.retryNumber = retryNumber + 1;

                log.debug(
                    `ScpCharacteristicClient: onResponseTimeout, there is no response from device for ${frame} frame. Try to write frame again, retry #${this.state.retryNumber}, skipErrorOnTimeout: ${skipErrorOnTimeout}`
                );

                this.writeFrames(true);
            } else {
                log.debug(
                    `ScpCharacteristicClient: onResponseTimeout, there is no response from device for ${frame} frame. skipErrorOnTimeout: ${skipErrorOnTimeout}`
                );

                if (skipErrorOnTimeout) {
                    this.state.responses.push('');
                    this.writeFrames(true);
                } else {
                    this.onFrameResponseError();
                }
            }
        } else {
            this.clearQueue();
        }
    };
}
